import { assign, fromPromise, raise, setup } from 'xstate';
import { showAxiosErrorAction } from '../actions';
import {
  addBotMessage,
  addUserMessage,
  clearError,
  closeSocket,
  resetConversation,
  setLoading,
  setTypingStatus,
  updateInput,
} from './actions';
import {
  createWebSocketConnection,
  disconnectWebSocket,
  sendMessageHttp,
  sendMessageWs,
  sendResetMessage,
} from './api';
import { Events } from './events';
import { ChatbotContext } from './types';

export const chatbotMachine = setup({
  actors: {
    createWebSocketConnection: fromPromise<WebSocket>(({ signal }) =>
      createWebSocketConnection({ signal })
    ),
    sendMessageWs: fromPromise(sendMessageWs),
    sendMessageHttp: fromPromise(sendMessageHttp),
    disconnectWebSocket: fromPromise(disconnectWebSocket),
    sendResetMessage: fromPromise(sendResetMessage),
  },
  types: {
    context: {} as ChatbotContext,
    events: {} as Events,
    input: {} as Partial<ChatbotContext>,
  },
  actions: {
    addUserMessage: assign(addUserMessage),
    addBotMessage: assign(addBotMessage),
    setTypingStatus: assign(setTypingStatus),
    updateInput: assign(updateInput),
    setLoading: assign(setLoading),
    clearError: assign(clearError),
    closeSocket: assign(closeSocket),
    resetConversation: assign(resetConversation),
    showErrorAction: showAxiosErrorAction,
  },
}).createMachine({
  id: 'chatbot',
  initial: '$_IDLE',
  context: ({ input }) => ({
    messages: [],
    inputValue: '',
    isLoading: false,
    isTyping: false,
    error: null,
    socket: null,
    ...input,
  }),
  on: {
    ON_SEND_SHARED_CONTEXT: {
      actions: assign(({ event }) => event.payload.values || {}),
    },
    ON_INPUT_CHANGE: { actions: ['updateInput'] },
    ON_RESET_CONVERSATION: { actions: ['resetConversation'] },
  },
  states: {
    $_IDLE: {
      on: {
        ON_CONNECT: [
          {
            // If socket already exists and is open, go directly to connected
            guard: ({ context }) => context.socket?.readyState === WebSocket.OPEN,
            target: '$_CONNECTED',
          },
          {
            // Otherwise, create new connection
            target: '$_CONNECTING',
          },
        ],
        ON_SEND_MESSAGE: {
          target: '$_SENDING_HTTP',
          actions: ['addUserMessage', 'setLoading'],
        },
      },
    },
    $_CONNECTING: {
      on: {
        // Leaving while the socket is still opening stops the invoke, which aborts it.
        ON_DISCONNECT: { target: '$_IDLE' },
      },
      invoke: {
        src: 'createWebSocketConnection',
        onDone: {
          target: '$_CONNECTED',
          actions: assign({
            socket: ({ event }) => event.output,
            error: null,
          }),
        },
        onError: {
          target: '$_ERROR',
          actions: ['showErrorAction'],
        },
      },
    },
    $_CONNECTED: {
      on: {
        ON_SEND_MESSAGE: [
          {
            guard: ({ context }) => !!context.socket,
            target: '$_SENDING_WS',
            actions: ['addUserMessage', 'setLoading'],
          },
          {
            target: '$_SENDING_HTTP',
            actions: ['addUserMessage', 'setLoading'],
          },
        ],
        ON_RECEIVE_MESSAGE: {
          actions: ['addBotMessage'],
        },
        ON_TYPING_STATUS: {
          actions: ['setTypingStatus'],
        },
        ON_DISCONNECT: {
          target: '$_IDLE',
          actions: ['closeSocket'],
        },
        ON_ERROR: {
          target: '$_ERROR',
          actions: ['showErrorAction'],
        },
      },
    },
    $_SENDING_WS: {
      invoke: {
        src: 'sendMessageWs',
        input: ({ context, event }) => {
          if (event.type !== 'ON_SEND_MESSAGE' || !context.socket) {
            throw new Error('Invalid state for WebSocket message');
          }
          return {
            message: event.payload.message,
            socket: context.socket,
          };
        },
        onDone: {
          target: '$_CONNECTED', // Return to connected state, wait for KB response
          actions: assign({ isLoading: false }),
        },
        onError: {
          target: '$_ERROR',
          actions: ['showErrorAction'],
        },
      },
    },
    $_SENDING_HTTP: {
      // Ensure a session id exists before the REST call so multi-turn history
      // works on this transport. If WebSocket has already set one (received via
      // chatbot.complete / chatbot.question), reuse it for cross-transport
      // continuity. Otherwise generate locally. Rotated on reset.
      entry: assign({
        sessionId: ({ context }) => context.sessionId ?? crypto.randomUUID(),
      }),
      invoke: {
        src: 'sendMessageHttp',
        input: ({ context, event }) => {
          if (event.type !== 'ON_SEND_MESSAGE') return { message: '' };
          return { message: event.payload.message, sessionId: context.sessionId };
        },
        onDone: {
          // Raise ON_RECEIVE_MESSAGE so the REST response flows through the
          // same addBotMessage reducer used by the WebSocket path. Keeps
          // clarification/PII/suggestedTopics flags and session_id updates
          // at parity across transports.
          target: '$_CONNECTED',
          actions: raise(({ event }) => ({
            type: 'ON_RECEIVE_MESSAGE' as const,
            payload: event.output,
          })),
        },
        onError: {
          target: '$_ERROR',
          actions: ['showErrorAction'],
        },
      },
    },
    $_ERROR: {
      on: {
        ON_CONNECT: {
          target: '$_CONNECTING',
          actions: ['clearError'],
        },
        ON_SEND_MESSAGE: {
          target: '$_SENDING_HTTP',
          actions: ['addUserMessage', 'setLoading', 'clearError'],
        },
      },
    },
  },
});
